import { capitalizeFirstLetter, classnames } from '@/helpers';
import { _t, getLang } from '@/locale';
import moment from 'moment';
import CalendarDay from './components/CalendarDay/CalendarDay';
import PickTimeViewBar from './components/PickTimeViewBar/PickTimeViewBar';
import { PickTimeCalendarProps } from './PickTimeCalendar.types';

/**
 * `_CVPickTimeCalendar` content view (MPA-2581, epic MPA-2598).
 *
 * Spec: marketplace-specs/specs/002-MPA-2598-weekpicker (reviewed 0e23647).
 * Figma: Iteration 10 — node 502-22208 (isNarrow=False 502-22209, isNarrow=True
 * 502-22315). A month-style calendar: a weekday header, month sections of
 * Monday-anchored week rows of day cells, and a month-view toggle + waitlist
 * link. Selecting a day navigates to the week grid view for the week containing
 * that day (legacy behaviour); the month/calendar view shows no per-day time
 * slots in the first release (see marketplace-specs decisions).
 *
 * Purely presentational — it renders the typed calendar data from
 * `PickTimeCalendar.helpers.ts` and reports the selected day through a callback;
 * data fetching and waitlist eligibility live outside the view.
 */
const PickTimeCalendar = ({
  title,
  calendarData,
  isNarrow = false,
  monthViewOn = true,
  showWaitlist = false,
  onSelectDay,
  onToggleMonthView,
  onOpenWaitlist,
}: PickTimeCalendarProps) => {
  const { months } = calendarData;
  // Format dates in the active app language. The global moment locale is pinned to
  // 'sv' (helpers/date.js), so localize per-instance instead of mutating it.
  const lang = getLang();
  const atLang = (input?: moment.MomentInput) => moment(input).locale(lang);
  // Localized, capitalized date label (moment returns lowercase month/weekday names for sv/fi).
  const fmt = (input: moment.MomentInput, pattern: string) => capitalizeFirstLetter(atLang(input).format(pattern));
  // Monday-first weekday labels (isoWeek), matching the Monday-anchored grid.
  const weekdayLabels = Array.from({ length: 7 }, (_, index) =>
    capitalizeFirstLetter(atLang().startOf('isoWeek').add(index, 'days').format('dd')),
  );
  const gridTemplateColumns = `${isNarrow ? '1.75rem' : '3rem'} repeat(7, minmax(0, 1fr))`;
  const gutter = isNarrow ? 'px-sm' : 'px-md';

  const weekdayHeader = (
    <div
      className={classnames('border-border_regular grid h-[48px] items-center border-b', gutter)}
      style={{ gridTemplateColumns }}>
      <span className={classnames('text-fg_secondary', isNarrow ? 'text-xs' : 'text-l')}>
        {_t('features.weekpicker.PickTimeCalendar.weekColumn')}
      </span>
      {weekdayLabels.map((label, index) => (
        <span key={index} className="text-fg_secondary text-l text-center">
          {label}
        </span>
      ))}
    </div>
  );

  const calendar = (
    <div className="gap-xs pt-md flex flex-1 flex-col overflow-y-auto">
      {months.map((month) => (
        <section key={`${month.year}-${month.month}`} aria-labelledby={`picktime-month-${month.year}-${month.month}`}>
          <div
            id={`picktime-month-${month.year}-${month.month}`}
            className={classnames(
              'bg-bg_secondary_neutral text-fg_primary text-l px-sm py-2xs rounded-sm',
              isNarrow ? 'mx-sm' : 'mx-md',
            )}>
            {fmt([month.year, month.month, 1], 'MMMM')}
          </div>
          {month.weeks.map((week, weekIndex) => (
            <div
              key={`${week.weekNumber}-${weekIndex}`}
              className={classnames('grid items-center', gutter)}
              style={{ gridTemplateColumns }}>
              <span className="text-fg_secondary text-xs">
                {_t('features.weekpicker.PickTimeCalendar.weekShort')} {week.weekNumber}
              </span>
              {week.days.map((day, dayIndex) =>
                day ? (
                  <CalendarDay
                    key={day.date}
                    dayNumber={day.dayNumber}
                    state={day.state}
                    isToday={day.isToday}
                    selectable={day.selectable}
                    ariaLabel={fmt(day.date, 'dddd D MMMM')}
                    onClick={() => onSelectDay?.(day.date)}
                  />
                ) : (
                  <span key={`empty-${dayIndex}`} className="h-[48px]" />
                ),
              )}
            </div>
          ))}
        </section>
      ))}
    </div>
  );

  const bottomBar = (
    <PickTimeViewBar
      monthViewOn={monthViewOn}
      showWaitlist={showWaitlist}
      isNarrow={isNarrow}
      onToggleMonthView={onToggleMonthView}
      onOpenWaitlist={onOpenWaitlist}
    />
  );

  return (
    <div role="region" aria-label={title} className="flex h-full w-full flex-col">
      <div
        className={classnames(
          'bg-surface_l2 border-border_regular flex min-h-0 flex-1 flex-col overflow-hidden',
          isNarrow ? 'border-b' : 'rounded-lg border',
        )}>
        {weekdayHeader}
        {calendar}
      </div>
      {bottomBar}
    </div>
  );
};

export default PickTimeCalendar;
